import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '../components/Navbar';
import Modal from '../components/Modal';
import api from '../api/client';

const TABS = ['Felhasználók', 'Invite kódok', 'Tranzakciók'];

export default function AdminPanel() {
  const [tab, setTab] = useState(0);

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold mb-6">Admin Panel</h1>

        <div className="flex gap-2 mb-6 border-b border-slate-700">
          {TABS.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              className={`pb-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                tab === i
                  ? 'border-yellow-400 text-yellow-400'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 0 && <UsersTab />}
        {tab === 1 && <InviteCodesTab />}
        {tab === 2 && <TransactionsTab />}
      </main>
    </div>
  );
}

/* ───────────────────────── USERS TAB ───────────────────────── */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [balanceModal, setBalanceModal] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/users').then((r) => setUsers(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function handleBan(user) {
    await api.post(`/admin/users/${user.id}/ban`);
    load();
  }

  return (
    <div>
      {loading ? (
        <p className="text-slate-400 text-sm">Betöltés...</p>
      ) : (
        <div className="card p-0 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-slate-700 text-slate-400">
                <th className="text-left px-4 py-3">Felhasználó</th>
                <th className="text-left px-4 py-3">Szerep</th>
                <th className="text-right px-4 py-3">Egyenleg</th>
                <th className="text-left px-4 py-3">Regisztrált</th>
                <th className="text-left px-4 py-3">Utolsó belépés</th>
                <th className="text-left px-4 py-3">Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="px-4 py-3">
                    <span className="font-medium">{u.username}</span>
                    {u.banned && <span className="badge-banned ml-2">Bannolva</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={u.role === 'admin' ? 'badge-admin' : 'badge-user'}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-yellow-400 font-semibold">
                    {Number(u.balance).toLocaleString('hu')} 💰
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {new Date(u.created_at).toLocaleDateString('hu')}
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('hu') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        className="btn-ghost text-xs py-1 px-2"
                        onClick={() => setBalanceModal(u)}
                      >
                        Egyenleg
                      </button>
                      <button
                        className={`text-xs py-1 px-2 btn ${u.banned ? 'btn-ghost' : 'btn-danger'}`}
                        onClick={() => handleBan(u)}
                      >
                        {u.banned ? 'Feloldás' : 'Ban'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {balanceModal && (
        <BalanceModal
          user={balanceModal}
          onClose={() => setBalanceModal(null)}
          onDone={() => { setBalanceModal(null); load(); }}
        />
      )}
    </div>
  );
}

function BalanceModal({ user, onClose, onDone }) {
  const [form, setForm] = useState({ amount: '', type: 'admin_grant', note: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/balance`, {
        amount: parseFloat(form.amount),
        type: form.type,
        note: form.note,
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Hiba');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title={`Egyenleg – ${user.username}`} onClose={onClose}>
      <p className="text-slate-400 text-sm mb-4">
        Jelenlegi egyenleg: <span className="text-yellow-400 font-semibold">{Number(user.balance).toLocaleString('hu')} 💰</span>
      </p>
      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Típus</label>
          <select
            className="input"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            <option value="admin_grant">Feltöltés (+)</option>
            <option value="admin_deduct">Levonás (−)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Összeg</label>
          <input
            className="input"
            type="number"
            min="1"
            step="any"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Megjegyzés (opcionális)</label>
          <input
            className="input"
            type="text"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button className="btn-ghost flex-1" type="button" onClick={onClose}>Mégse</button>
          <button className="btn-gold flex-1" type="submit" disabled={loading}>
            {loading ? '...' : 'Mentés'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ───────────────────────── INVITE CODES TAB ───────────────────────── */
function InviteCodesTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genModal, setGenModal] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/invite-codes').then((r) => setCodes(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-slate-400 text-sm">{codes.length} kód összesen</p>
        <button className="btn-gold text-sm" onClick={() => setGenModal(true)}>+ Kód generálás</button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Betöltés...</p>
      ) : (
        <div className="card p-0 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead>
              <tr className="border-b border-slate-700 text-slate-400">
                <th className="text-left px-4 py-3">Kód</th>
                <th className="text-left px-4 py-3">Létrehozta</th>
                <th className="text-left px-4 py-3">Felhasználta</th>
                <th className="text-left px-4 py-3">Lejárat</th>
                <th className="text-left px-4 py-3">Állapot</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const used = !!c.used_by;
                const expired = c.expires_at && new Date(c.expires_at) < new Date();
                return (
                  <tr key={c.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="px-4 py-3 font-mono text-yellow-300">{c.code}</td>
                    <td className="px-4 py-3 text-slate-300">{c.created_by_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-300">{c.used_by_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-400">
                      {c.expires_at ? new Date(c.expires_at).toLocaleDateString('hu') : '∞'}
                    </td>
                    <td className="px-4 py-3">
                      {used ? (
                        <span className="badge bg-slate-600 text-slate-300">Felhasználva</span>
                      ) : expired ? (
                        <span className="badge-banned">Lejárt</span>
                      ) : (
                        <span className="badge-green">Szabad</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {genModal && (
        <GenerateModal
          onClose={() => setGenModal(false)}
          onDone={() => { setGenModal(false); load(); }}
        />
      )}
    </div>
  );
}

function GenerateModal({ onClose, onDone }) {
  const [form, setForm] = useState({ count: 1, expiresAt: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/admin/invite-codes', {
        count: parseInt(form.count),
        expiresAt: form.expiresAt || null,
      });
      setResult(res.data);
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return (
      <Modal title="Generált kódok" onClose={onDone}>
        <div className="space-y-2 mb-4">
          {result.map((c) => (
            <div key={c.id} className="bg-slate-700 rounded-lg px-3 py-2 font-mono text-yellow-300 text-sm flex items-center justify-between">
              <span>{c.code}</span>
              <button
                className="text-slate-400 hover:text-white text-xs ml-4"
                onClick={() => navigator.clipboard.writeText(c.code)}
              >
                Másolás
              </button>
            </div>
          ))}
        </div>
        <button className="btn-gold w-full" onClick={onDone}>Kész</button>
      </Modal>
    );
  }

  return (
    <Modal title="Invite kódok generálása" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Darabszám (max 50)</label>
          <input
            className="input"
            type="number"
            min="1"
            max="50"
            value={form.count}
            onChange={(e) => setForm({ ...form, count: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Lejárat (opcionális)</label>
          <input
            className="input"
            type="datetime-local"
            value={form.expiresAt}
            onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button className="btn-ghost flex-1" type="button" onClick={onClose}>Mégse</button>
          <button className="btn-gold flex-1" type="submit" disabled={loading}>
            {loading ? '...' : 'Generálás'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ───────────────────────── TRANSACTIONS TAB ───────────────────────── */
const TX_TYPES = ['', 'admin_grant', 'admin_deduct', 'deposit', 'withdraw', 'win', 'loss'];
const TX_LABELS = {
  '': 'Mind',
  admin_grant: 'Admin feltöltés',
  admin_deduct: 'Admin levonás',
  deposit: 'Befizetés',
  withdraw: 'Kifizetés',
  win: 'Nyeremény',
  loss: 'Veszteség',
};

function TransactionsTab() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', from: '', to: '' });

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.type) params.type = filters.type;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    api.get('/admin/transactions', { params }).then((r) => setTxs(r.data)).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {/* Szűrők */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          className="input w-auto"
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
        >
          {TX_TYPES.map((t) => <option key={t} value={t}>{TX_LABELS[t]}</option>)}
        </select>
        <input
          className="input w-auto"
          type="date"
          value={filters.from}
          onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          placeholder="Dátumtól"
        />
        <input
          className="input w-auto"
          type="date"
          value={filters.to}
          onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          placeholder="Dátumig"
        />
        <button className="btn-ghost text-sm" onClick={() => setFilters({ type: '', from: '', to: '' })}>
          Törlés
        </button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Betöltés...</p>
      ) : (
        <div className="card p-0 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead>
              <tr className="border-b border-slate-700 text-slate-400">
                <th className="text-left px-4 py-3">Felhasználó</th>
                <th className="text-left px-4 py-3">Típus</th>
                <th className="text-right px-4 py-3">Összeg</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Megjegyzés</th>
                <th className="text-left px-4 py-3">Dátum</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((tx) => (
                <tr key={tx.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="px-4 py-3 font-medium">{tx.username}</td>
                  <td className="px-4 py-3 text-slate-300">{TX_LABELS[tx.type] || tx.type}</td>
                  <td className={`px-4 py-3 text-right font-semibold ${parseFloat(tx.amount) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {parseFloat(tx.amount) >= 0 ? '+' : ''}{Number(tx.amount).toLocaleString('hu')} 💰
                  </td>
                  <td className="px-4 py-3 text-slate-400 hidden sm:table-cell">{tx.note || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {new Date(tx.created_at).toLocaleDateString('hu', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </td>
                </tr>
              ))}
              {txs.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Nincs találat</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
